import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Badge } from '@capawesome/capacitor-badge';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of, from, Observable, combineLatest, defer } from 'rxjs';
import {
  map,
  catchError,
  withLatestFrom,
  concatMap,
  filter,
  switchMap,
  take,
} from 'rxjs/operators';
import * as AuthActions from 'store/auth-store/auth.actions';
import * as ActivationActions from 'store/activations-store/activations.actions';
import * as fromActivations from 'store/activations-store/activations.reducer';
import * as MedicationIntakesActions from 'store/medication-intakes-store/medication-intakes.actions';
import * as fromMedicationIntakes from 'store/medication-intakes-store/medication-intakes.reducer';
import * as MedicationActions from 'store/medications-store/medications.actions';
import * as fromMedications from 'store/medications-store/medications.reducer';
import * as MeasurementActions from 'store/measurements-store/measurements.actions';
import * as fromMeasurements from 'store/measurements-store/measurements.reducer';
import * as fromMonitoringGoals from 'store/monitoring-goals-store/monitoring-goals.reducer';
import * as NotificationActions from './notifications.actions';
import * as MonitoringGoalActions from 'store/monitoring-goals-store/monitoring-goals.actions';
import * as fromNotifications from './notifications.reducer';
import {
  Activation,
  EcgGoal,
  Measurement,
  MedicationIntake,
  SurveyGoal,
  medicationIntakeTimeOfDay,
} from 'app/models';
import { SubscriptionCategoryPipe } from 'app/shared/pipe/subscription-category.pipe';
import {
  LocalNotificationSchema,
  LocalNotifications,
  PendingResult,
} from '@capacitor/local-notifications';
import { NotificationType, ILocalNotificationType } from './notifications.model';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrAfter);

export const DEFAULT_MED_INTAKES_NOTIFICATION_TIMES_FOR_PART_OF_DAY: Record<
  medicationIntakeTimeOfDay,
  string
> = {
  morning: '08:00',
  noon: '12:00',
  evening: '18:00',
  night: '22:00',
} as const;

export const DEFAULT_MEASSUREMENT_TIME: string = '11:00' as const;
export const MAX_DAYS_IN_FUTURE_FOR_NOTIFICATIONS = 7;

@Injectable()
export class NotificationsEffects {
  // actions necessary for initialization
  private requiredActionsToInit = [
    [
      MedicationIntakesActions.loadMedicationIntakesSuccess,
      MedicationIntakesActions.loadMedicationIntakesFailure,
    ],
    [MedicationActions.loadMedicationsSuccess, MedicationActions.loadMedicationsFailure],
    [
      MonitoringGoalActions.loadMonitoringGoalsSuccess,
      MonitoringGoalActions.loadMonitoringGoalsFailure,
    ],
    [MeasurementActions.loadMeasurementsSuccess, MeasurementActions.loadMeasurementsFailure],
    [ActivationActions.loadActivationsSuccess, ActivationActions.loadActivationsFailure],
  ];

  constructor(
    private actions$: Actions,
    private store: Store<
      | fromNotifications.State
      | fromMedicationIntakes.State
      | fromMedications.State
      | fromMeasurements.State
      | fromMonitoringGoals.State
    >,
    private platform: Platform,
    private subscriptionCategoryPipe: SubscriptionCategoryPipe
  ) {}

  initBadge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateSuccess, AuthActions.addSignInDataSuccess),
      switchMap(() =>
        this.getBadgeRequest().pipe(map(() => NotificationActions.initBadgeSuccess()))
      )
    );
  });

  waitForRequiredActionsToFinish$ = createEffect(() =>
    combineLatest(
      this.requiredActionsToInit.map(([success, failure]) =>
        this.actions$.pipe(ofType(success, failure))
      )
    ).pipe(
      withLatestFrom(this.store.select(fromNotifications.selectInitialized)),
      filter(([, isInitialized]) => isInitialized === false),
      map(() => NotificationActions.notificationsReady())
    )
  );

  rescheduleNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        NotificationActions.notificationsReady,
        // reschedule in case medication changed for time that was previously with or without medication
        MedicationActions.loadMedicationsSuccess,
        MedicationActions.createMedicationSuccess,
        MedicationActions.deleteMedicationSuccess,
        // reschedule in case medication was taken to remove medication notifications
        MedicationIntakesActions.loadMedicationIntakesSuccess,
        MedicationIntakesActions.updateMedicationIntakeSuccess,
        MedicationIntakesActions.updateMedicationIntakesSuccess,
        MedicationIntakesActions.loadTodaysMedicationIntakesSuccess,
        // reschedule in case measurement was made
        MeasurementActions.addMeasurementsSuccess,
        MeasurementActions.loadMeasurementsSuccess,
        // reschedule in case monitoring goals were updated (e.g. new survey arrived)
        MonitoringGoalActions.loadMonitoringGoalsSuccess,
        ActivationActions.loadActivations,
        ActivationActions.loadActivationsSuccess,
        NotificationActions.addCustomNotifications,
        NotificationActions.deleteCustomNotificationsSuccess
      ),
      withLatestFrom(
        this.store.select(fromNotifications.selectIds),
        this.store.select(fromNotifications.selectInitialized)
      ),
      filter(([, , isInitialized]) => isInitialized === true),
      switchMap(([action, ids]) =>
        this.cancelCurrentNotifications(ids).pipe(
          map(() =>
            // deleteNotificationsSuccess deletes all notifications but custom ones
            action.type === NotificationActions.addCustomNotifications.type
              ? NotificationActions.deleteNotificationsSuccess({
                  notifications: action.notifications.map((notification) => ({
                    ...notification,
                    extra: { ...notification.extra, type: NotificationType.Custom },
                  })),
                })
              : NotificationActions.deleteNotificationsSuccess({})
          ),
          catchError((error) => of(NotificationActions.deleteNotificationsFailure({ error })))
        )
      )
    );
  });

  /**
   * fetches all pending notifications and adds them to the ngrx store
   */
  addPendingNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.addPendingNotifications),
      switchMap(() =>
        this.getPendingNotificationsRequest().pipe(
          map((pendingResult: PendingResult) =>
            NotificationActions.addPendingNotificationsSuccess({
              notifications: pendingResult.notifications.map((notification) => ({
                ...notification,
                // schedule at property needs to be converted to Date,
                // otherwise wrong Date format error is thrown if notification is dispatched
                schedule: { ...notification.schedule, at: new Date(notification.schedule.at) },
              })),
            })
          ),
          catchError((error) => of(NotificationActions.addPendingNotificationsFailure({ error })))
        )
      )
    );
  });

  deleteCustomNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.deleteCustomNotifications),
      switchMap((action) =>
        this.cancelCurrentNotifications(
          action?.notifications.map((notification) => notification.id)
        ).pipe(
          map(() =>
            NotificationActions.deleteCustomNotificationsSuccess({
              notifications: action.notifications,
            })
          ),
          catchError((error) => of(NotificationActions.deleteCustomNotificationsFailure({ error })))
        )
      )
    );
  });

  loadNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.deleteNotificationsSuccess),
      concatMap((action) =>
        this.isLocalNotificationsEnabled().pipe(
          take(1),
          withLatestFrom(
            this.store.pipe(
              select(fromMedicationIntakes.selectMedicationIntakesAllWithoutOnDemand),
              map((intakes) => intakes.filter((intake) => intake.status === 'UNKNOWN'))
            ),
            this.store.pipe(select(fromMeasurements.selectMeasurementsFromTodayWithoutEcg)),
            this.store.pipe(select(fromMonitoringGoals.selectEcgMonitoringGoal)),
            this.store.pipe(select(fromMonitoringGoals.selectSurveyMonitoringGoal)),
            this.store.pipe(select(fromMedications.selectHasMedicationForPartOfDay)),
            this.store.pipe(select(fromActivations.selectCurrentActivation)),
            this.store.pipe(select(fromActivations.selectHasEnqueuedActivations)),
            this.store.pipe(select(fromNotifications.selectCustomNotifications))
          ),
          concatMap(
            ([
              isNotificationsEnabled,
              medicationIntakes,
              measurements,
              ecgGoal,
              surveyGoal,
              hasMedicationForPartOfDay,
              currentActivation,
              hasEnqueuedActivations,
              storedCustomNotifications,
            ]) => {
              // no notificiations for tmc_eheart subscriptions
              if (currentActivation?.category === 'tmc_eheart') {
                return of(NotificationActions.loadNotificationsSuccess({ notifications: [] }));
              }

              const dailyNotifications = this.getDailyNotifications(
                measurements,
                medicationIntakes,
                ecgGoal,
                surveyGoal,
                hasMedicationForPartOfDay
              );

              const customNotifications =
                action.notifications !== undefined
                  ? [...storedCustomNotifications, ...action.notifications]
                  : [...storedCustomNotifications];

              // adds ids to the new notifications to schedule
              // filters notifications from the past (may happen with custom ones)
              const notifications = [
                ...customNotifications,
                ...dailyNotifications,
                ...this.getAppNotifications(),
                ...this.getSubscriptionNotifications(currentActivation, hasEnqueuedActivations),
              ]
                .reduce(
                  (prev, curr, i) => [...prev, { id: i, ...curr }],
                  [] as LocalNotificationSchema[]
                )
                .filter((notification) =>
                  dayjs(notification.schedule?.at).isSameOrAfter(dayjs(), 'milliseconds')
                );

              if (notifications.length > 64) {
                console.error(
                  `${notifications.length} notifications exceeds iOS limit of 64 notifications per app.`
                );
              }

              if (!isNotificationsEnabled || notifications.length === 0) {
                return of(NotificationActions.loadNotificationsSuccess({ notifications }));
              }

              return this.isExactAllowed().pipe(
                concatMap((isExactAllowed) => {
                  const notificationsExtendet = notifications.map((notification) => ({
                    ...notification,
                    isExactNotification: isExactAllowed,
                  }));

                  LocalNotifications.schedule({
                    notifications: notificationsExtendet,
                  });

                  return of(
                    NotificationActions.loadNotificationsSuccess({
                      notifications: notificationsExtendet,
                    })
                  );
                }),
                map(() => NotificationActions.loadNotificationsSuccess({ notifications })),
                catchError((error) => {
                  console.error('Scheduling notifications failed', error);
                  return of(NotificationActions.loadNotificationsSuccess({ notifications: [] }));
                })
              );
            }
          )
        )
      )
    );
  });

  /**
   * daily notifications (measurements, medication intakes, surveys)
   */
  private getDailyNotifications(
    measurements: Measurement[],
    medicationIntakes: MedicationIntake[],
    ecgGoal: EcgGoal,
    surveyGoal: SurveyGoal,
    hasMedicationForPartOfDay: medicationIntakeTimeOfDay[]
  ): ILocalNotificationType[] {
    const today = dayjs().format('YYYY-MM-DD');
    const openMeasurements = this.hasMissingMeasurement(measurements, ecgGoal);
    const openSurveys = surveyGoal?.open_measurements?.length > 0;

    // merges the times for planned daily notifications, sorts the times and removes dublicates
    const dailyNotificationTimes = [
      ...new Set([
        ...hasMedicationForPartOfDay.map(
          (partOfDay) => DEFAULT_MED_INTAKES_NOTIFICATION_TIMES_FOR_PART_OF_DAY[partOfDay]
        ),
        DEFAULT_MEASSUREMENT_TIME,
      ]),
    ].sort();

    const notificationBasics: {
      time: string;
      date: string;
      actionsRequired: NotificationType[];
    }[] = [];

    for (let dayCount = 0; dayCount <= MAX_DAYS_IN_FUTURE_FOR_NOTIFICATIONS; dayCount++) {
      const date = dayjs(today).add(dayCount, 'day').format('YYYY-MM-DD');

      dailyNotificationTimes.map((time) => {
        const base = {
          time,
          date,
          actionsRequired: [] as NotificationType[],
        };

        if (openSurveys) {
          base.actionsRequired.push(NotificationType.Survey);
        }

        // checks if it's time to include an open measurement notifications
        // measurements for future days are always undone
        if (
          time >= DEFAULT_MEASSUREMENT_TIME &&
          ((date === today && openMeasurements) || date !== today)
        ) {
          base.actionsRequired.push(NotificationType.Measurement);
        }

        // checks if there are open medication intakes for the past day
        if (this.hasMissingMedicationIntakesForDateTime(medicationIntakes, date, time)) {
          base.actionsRequired.push(NotificationType.Medication);
        }
        notificationBasics.push(base);
      });
    }

    // notification text content
    const translations: Partial<Record<NotificationType, string>> = {
      [NotificationType.Measurement]: $localize`Vitalwerte`,
      [NotificationType.Survey]: $localize`Fragebögen`,
      [NotificationType.Medication]: $localize`Medikamente`,
    };

    return notificationBasics
      .filter((notificationBase) => notificationBase.actionsRequired.length > 0)
      .map((notificationBase) => {
        const actions = notificationBase.actionsRequired;
        let title = $localize`Behalte den Überblick`;
        let body = '';

        if (actions.length === 1) {
          switch (actions[0]) {
            case NotificationType.Measurement:
              title = $localize`Messung und Gesundheitscheck nicht verpassen`;
              body = $localize`Nimm Dir kurz Zeit für Deine Messungen und den täglichen Gesundheitscheck.`;
              break;

            case NotificationType.Survey: // just a fallback, if only missing a survey, no notification is created
              title = $localize`Fragebögen ausfüllen`;
              body = $localize`Nimm Dir kurz Zeit und fülle die offenen Fragebögen aus`;
              break;

            case NotificationType.Medication:
              title = $localize`Zeit für Medikamente`;
              body = $localize`Denke an die Einnahme Deiner Medikamente.`;
              break;

            default:
              body = $localize`Sieh nach, was auf der Übersichtsseite noch offen ist.`;
              break;
          }
        } else if (actions.length === 2) {
          body = $localize`${translations[actions[0]]} und ${
            translations[actions[1]]
          } - Sieh nach, was auf der Übersichtsseite noch offen ist.`;
        } else if (actions.length > 2) {
          body = $localize`Sieh nach, was auf der Übersichtsseite noch offen ist.`;
        }

        return {
          title,
          body,
          extra: {
            type: NotificationType.Daily,
          },
          schedule: {
            at: dayjs(
              `${notificationBase?.date} ${notificationBase?.time}`,
              'YYYY-MM-DD HH:mm'
            ).toDate(),
          },
        };
      });
  }

  /**
   * if the user has been inactive for a long period of time, notifications are sent 8 and 12 days after the last app use
   */
  private getAppNotifications(): ILocalNotificationType[] {
    return [
      {
        daysToAdd: 8,
        title: 'Starte jetzt',
        body: 'Öffne die App und tu etwas für Dein Herz.',
      },
      {
        daysToAdd: 12,
        title: 'Dranbleiben - Jetzt starten',
        body: 'Bleib am Ball und öffne die App.',
      },
    ].map((details) => {
      const date = dayjs().add(details.daysToAdd, 'days').format('YYYY-MM-DD');

      return {
        title: details.title,
        body: details.body,
        extra: { type: NotificationType.App },
        schedule: { at: dayjs(`${date} 14:00}`, 'YYYY-MM-DD HH:mm').toDate() },
      };
    });
  }

  /**
   * reminds the user of an expiring subscription 14, 5 and 1 day(s) before the subscription becomes invalid.
   * no notifications are scheduled if there is a enqueued app subscription
   */
  private getSubscriptionNotifications(
    currentSubscription: Activation,
    hasEnqueuedActivations: boolean
  ): ILocalNotificationType[] {
    return hasEnqueuedActivations || !currentSubscription
      ? []
      : [14, 5, 1].map((daysBeforeExpiration) => {
          const at = dayjs(currentSubscription.end_date)
            .subtract(daysBeforeExpiration, 'days')
            .set('hour', 14)
            .set('seconds', 0)
            .toDate();

          return {
            title: $localize`${this.subscriptionCategoryPipe.transform(
              currentSubscription.category
            )} läuft in ${daysBeforeExpiration} ${daysBeforeExpiration > 1 ? ' Tagen' : ' Tag'} ab`,
            body: $localize`Bitte (neuen) Freischalt-/Aktivierungscode in der ProHerz-App eingeben.`,
            extra: { type: NotificationType.Subscription },
            schedule: { at },
          };
        });
  }

  private hasMissingMeasurement(measurements: Measurement[], ecgGoal: EcgGoal) {
    // checks for an open ecg measurement for today
    // always false if the ecg-goal is undefined
    const hasOpenEcgMeasurment = ecgGoal
      ? ecgGoal?.last_results[0] && dayjs().isSame(ecgGoal.last_results[0].device_time, 'day')
        ? false
        : true
      : false;

    return (
      measurements.filter(
        // a missing temperature measurement should not raise a notification
        // cosnider only activated measurement-results
        (measurement) =>
          measurement.type !== 'temperature' &&
          measurement.results.filter((measurement) => measurement.deactivated_at === null)
            .length === 0
      )?.length > 0 || hasOpenEcgMeasurment
    );
  }

  private hasMissingMedicationIntakesForDateTime(
    medicationIntakes: MedicationIntake[],
    date: string,
    time: string
  ) {
    return (
      medicationIntakes
        .filter((medicationIntake) => medicationIntake.date === dayjs(date).format('YYYY-MM-DD'))
        .filter(
          (intake) =>
            DEFAULT_MED_INTAKES_NOTIFICATION_TIMES_FOR_PART_OF_DAY[intake.part_of_day] <= time
        )?.length > 0
    );
  }

  ngrxOnInitEffects(): any {
    return NotificationActions.addPendingNotifications();
  }

  private getPendingNotificationsRequest(): Observable<PendingResult> {
    return this.isLocalNotificationsEnabled().pipe(
      concatMap((isEnabled) =>
        isEnabled ? from(LocalNotifications.getPending()) : of({ notifications: [] })
      )
    );
  }

  private getBadgeRequest() {
    return from(Badge.checkPermissions()).pipe(
      concatMap((r) =>
        r.display === 'denied' || r.display === 'granted' ? of(r) : from(Badge.requestPermissions())
      )
    );
  }

  /**
   * cancels the given notifications
   */
  private cancelCurrentNotifications(ids: (string | number)[]) {
    const notifications = ids.reduce(
      (prev, curr) => [...prev, { id: +curr }],
      [] as LocalNotificationSchema[]
    );

    return this.isLocalNotificationsEnabled().pipe(
      concatMap((isEnabled) =>
        isEnabled && notifications.length > 0
          ? (from(LocalNotifications.cancel({ notifications })) as Observable<undefined>)
          : of({})
      )
    );
  }

  /**
   * checks if the local notifications are enabled on the users devise
   * requests permissions if not granted
   * alwas returns false on non-hybrid platforms.
   */
  private isLocalNotificationsEnabled() {
    return this.platform.is('hybrid') === false
      ? of(false)
      : from(LocalNotifications.checkPermissions()).pipe(
          concatMap((permissions) =>
            permissions.display === 'granted'
              ? of(true)
              : permissions.display === 'denied'
              ? of(false)
              : from(LocalNotifications.requestPermissions()).pipe(
                  map((result) => result.display === 'granted')
                )
          )
        );
  }

  /**
   * checks if the exact alarm settings are enabled on the users devise
   * alwas returns false on non-hybrid platforms.
   */
  private isExactAllowed(): Observable<boolean> {
    if (!this.platform.is('android')) {
      return of(true);
    }

    return defer(() => LocalNotifications.checkExactNotificationSetting()).pipe(
      map(({ exact_alarm }) => exact_alarm === 'granted'),
      catchError((error) => {
        console.error('Exact alarm permission check failed', error);
        return of(false);
      })
    );
  }
}
